import React from 'react';
import dayjs from 'dayjs';
import 'dayjs/locale/es';
import 'dayjs/locale/pt-br';
import { currentLanguage$ } from '../utils/language-utils';

/**
 * Servicio de internacionalización para componentes de fecha
 * Proporciona traducciones y configuración de locale para dayjs
 */

// Mapeo de idiomas a locales de dayjs
const LOCALE_MAP = {
  'es': 'es',
  'es-AR': 'es', 
  'pt-BR': 'pt-br'
};

// Traducciones personalizadas para días de la semana (abreviados)
const WEEKDAY_TRANSLATIONS = {
  'es': ['Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb', 'Dom'],
  'es-AR': ['Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb', 'Dom'],
  'pt-BR': ['Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb', 'Dom']
};

// Traducciones para meses
const MONTH_TRANSLATIONS = {
  'es': [
    'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
    'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'
  ],
  'es-AR': [
    'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
    'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'
  ],
  'pt-BR': [
    'Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho',
    'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro'
  ]
};

/**
 * Configura el locale de dayjs según el idioma
 * @param {string} language - Código de idioma (es, es-AR, pt-BR)
 */
export const configureDayjsLocale = (language) => {
  const locale = LOCALE_MAP[language] || 'es';
  dayjs.locale(locale);
};

/**
 * Obtiene los días de la semana traducidos
 * @param {string} language - Código de idioma
 * @returns {string[]} Array de días de la semana abreviados
 */
export const getWeekdays = (language) => {
  return WEEKDAY_TRANSLATIONS[language] || WEEKDAY_TRANSLATIONS['es'];
};

/**
 * Obtiene los meses traducidos
 * @param {string} language - Código de idioma
 * @returns {string[]} Array de nombres de meses
 */
export const getMonths = (language) => {
  return MONTH_TRANSLATIONS[language] || MONTH_TRANSLATIONS['es'];
};

/**
 * Formatea una fecha según el idioma actual
 * @param {Date|string} date - Fecha a formatear
 * @param {string} language - Código de idioma
 * @param {string} format - Formato deseado (por defecto 'D MMM YYYY')
 * @returns {string} Fecha formateada
 */
export const formatDate = (date, language, format = 'D MMM YYYY') => {
  if (!date) return '';
  
  const locale = LOCALE_MAP[language] || 'es';
  return dayjs(date).locale(locale).format(format);
};

/**
 * Hook personalizado para obtener las utilidades de idioma
 * Incluye configuración automática de dayjs
 */
export const useLanguageService = () => {
  const [currentLanguage, setCurrentLanguage] = React.useState('es');

  React.useEffect(() => {
    // Suscribirse a cambios de idioma
    const subscription = currentLanguage$.subscribe((language) => {
      setCurrentLanguage(language);
      configureDayjsLocale(language);
    });

    return () => subscription.unsubscribe();
  }, []);

  return {
    language: currentLanguage,
    weekdays: getWeekdays(currentLanguage),
    months: getMonths(currentLanguage),
    formatDate: (date, format) => formatDate(date, currentLanguage, format),
    configureDayjs: () => configureDayjsLocale(currentLanguage)
  };
};

// Inicialización automática del servicio
let isInitialized = false;

export const initializeLanguageService = () => {
  if (isInitialized) return;
  
  // Configurar idioma inicial
  const subscription = currentLanguage$.subscribe((language) => {
    configureDayjsLocale(language);
  });
  
  isInitialized = true;
  
  // No necesitamos unsubscribe aquí porque es un servicio global
  return subscription;
};

// Auto-inicializar cuando se importa el módulo
initializeLanguageService();
